import { debounce } from 'lodash';
import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Clickable,
  Tabs,
  Icon,
} from '@fashionnovateam/nova-ui-toolkit';
import Page from '../Page';
import Header from '../Header';
import AddTagsDrawer from './AddTags/AddTagDrawer';
import { SampleTagsList } from './SampleTagsList/SampleTagsList';
import { useParamFilter } from '../../Lib/Hooks/Filters/useParamFilter';
import { possibleParams, getAllowedSampleTypes } from './AddTags/constants';
import { useGetMetaDatas } from '../../Lib/Hooks/MetadataService';
import { User } from '../../Lib/Api/vendor';

function SampleTags({ user }: { user: User }) {
  const { id: userId } = useParams<{ id: string }>();
  const [searchValue, setSearchValue] = useState('');
  const [isAddTagsOpened, setIsAddTagsOpened] = useState(false);
  const { paramFilters, setParamFilter } = useParamFilter(possibleParams);

  const { data: metadatas } = useGetMetaDatas(['ns_master_vendorsMerchType']);

  const vendorMerchType = useMemo(() => {
    if (metadatas?.ns_master_vendorsMerchType && user?.vendor?.internal_id) {
      const vendorFound = metadatas.ns_master_vendorsMerchType.find(
        (vendor) => vendor.vendorId === user?.vendor?.internal_id,
      );
      return vendorFound?.merchType ?? null;
    }
    return null;
  }, [metadatas?.ns_master_vendorsMerchType, user?.vendor?.internal_id]);

  const allowedSampleTypes = getAllowedSampleTypes(
    vendorMerchType || undefined,
  );

  const onAddTagsClick = () => {
    setIsAddTagsOpened(true);
  };

  return (
    <Page.Container data-testid="sample-tags-container">
      <Header.Major icon="tag" title="Sample Tags">
        <Button data-testid="add-tags-button" onClick={onAddTagsClick}>
          <Icon variant="solid" name="plus/plus-circle" />
          Create New
        </Button>
      </Header.Major>
      <Header.Minor
        search={{
          value: searchValue,
          onChange: debounce((value) => setSearchValue(value), 500),
        }}
      >
        <Tabs.Group type="segment" data-testid="sample-tags-tabs">
          <Clickable
            data-testid="all-tab-button"
            onClick={() => setParamFilter('type_of_sample_tag', undefined)}
          >
            <Tabs.Item
              isActive={!paramFilters.type_of_sample_tag}
              type="segment"
              data-testid="all-tab"
            >
              All
            </Tabs.Item>
          </Clickable>
          {allowedSampleTypes.map((sampleType) => (
            <Clickable
              key={sampleType}
              data-testid={`${sampleType.toLowerCase().replace(/\s+/g, '-')}-tab-button`}
              onClick={() => setParamFilter('type_of_sample_tag', sampleType)}
            >
              <Tabs.Item
                isActive={paramFilters.type_of_sample_tag === sampleType}
                type="segment"
                data-testid={`${sampleType.toLowerCase().replace(/\s+/g, '-')}-tab`}
              >
                {sampleType}
              </Tabs.Item>
            </Clickable>
          ))}
        </Tabs.Group>
      </Header.Minor>

      <Page.Content>
        {userId && (
          <SampleTagsList
            userId={userId}
            onCreateNew={onAddTagsClick}
            paramFilters={paramFilters}
            searchValue={searchValue}
            domesticMerchPoEligible={user.vendor?.domestic_merch_po_eligible}
            allowedSampleTypes={allowedSampleTypes}
          />
        )}
      </Page.Content>

      <AddTagsDrawer
        isAddTagsOpened={isAddTagsOpened}
        setIsAddTagsOpened={setIsAddTagsOpened}
        domesticMerchPoEligible={user.vendor?.domestic_merch_po_eligible}
        allowedSampleTypes={allowedSampleTypes}
        onCreateNew={onAddTagsClick}
      />
    </Page.Container>
  );
}

export default SampleTags;
